import React, {
  PropsWithChildren,
  TouchEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactModal from 'react-modal';

import { css, Global } from '@emotion/react';

import { Box } from '../../core';
import { useDevice } from '../../hooks/useMedia';
import { ColorScheme, useTheme } from '../../system/theme';
import { colors as colorKeys } from '../../theme/novo';
import { ThemedLite } from '../../theme/ODThemeLite';
import { ThemedProps } from '../../theme/ThemedProps';
import { getNavigationBarTitleId } from './NavigationBar';
import { ModalA11yProps, ModalHeader, useDisabledBodyScroll } from './shared';

type ContextSheetBaseProps = PropsWithChildren<{
  /** Whether the context sheet is open */
  isOpen: boolean;
  /** Called when either the overlay or the X button is clicked */
  onRequestClose?: () => void;
  /** Called when the back button is clicked (back button only rendered if provided) */
  onBackClick?: () => void;
  /**
   * If the content of the context sheet changes (e.g., when you press the back button),
   * supply a key to uniquely identify each screen of content. This lets the component
   * animate the transition
   */
  contentKey?: string;
  /** Title of the context sheet */
  title?: string;
  /** Subtitle of the context sheet */
  subtitle?: string;
  /**  Header icons variant: icon has a circle & ghost is no circle. Defaults to icon  */
  headerIconButtonVariant?: 'icon' | 'ghost';
  /** Optional id for the modal header */
  id?: string;
  /** Option to show scrollbar - defaults to show scrollbar */
  showScrollbar?: boolean;
  /** Option to have padding - defaults to true*/
  hasPadding?: boolean;
  /**
   * Whether the context sheet should be centered on tablet and desktop instead of
   * anchored to the bottom (e.g., because the content is too short on larger screens).
   */
  tabletAndDesktopCentered?: boolean;
  /**
   * Set the modal to use a custom theme - if undefined it defaults to ThemeLite's default theme
   */
  themeProps?: ThemedProps;
  /**
   * Sets the content background color
   */
  contentBgColor?: ColorScheme;
}>;

interface ContextSheetAnalyticsProps {
  /**
   * Name of the context sheet in analytics events, should be unique within the app
   * Convention: {appName}-{contextSheetName}
   */
  analyticsName: string;
  /** Whether to track impression events for the header box */
  trackBoxImpression?: boolean;
  /** Whether to track impression events for the header buttons */
  trackButtonImpressions?: boolean;
}

// To meet accessibility standards, the ContextSheet component must receive either title, aria-labelledby, or aria-label.
type TitleProps = { title: string };
type NoTitleProps = { title?: never };
type ContextSheetA11yProps = ModalA11yProps<TitleProps, NoTitleProps>;

export type ContextSheetProps = ContextSheetBaseProps &
  ContextSheetAnalyticsProps &
  ContextSheetA11yProps;

const CLOSE_TIMEOUT_MS = 500;
const CONTENT_CLASS_NAME = {
  base: 'BricksContextSheet',
  afterOpen: 'BricksContextSheet--after-open',
  beforeClose: 'BricksContextSheet--before-close',
};
const OVERLAY_CLASS_NAME = {
  base: 'BricksContextSheet__Overlay',
  afterOpen: 'BricksContextSheet__Overlay--after-open',
  beforeClose: 'BricksContextSheet__Overlay--before-close',
};
const MARGIN_TOP_MOBILE = 48;
const HEIGHT_TRANSITION_MS = 250;
const OPACITY_TRANSITION_MS = 200;

/**
 * These are our default modal patterns for mobile. Context sheets don’t cover
 * the whole screen but rather maintain a peek of a source view. This helps
 * users understand where they came from and quickly navigate back if they need
 * to. Also called Bottom Sheets via Google Materials
 *
 * Content Sizing
 *
 *
 * ------------------
 *
 * ContextSheets change size based on their content. On mobile, their height
 * expands up to 48px from the top of the screen and on larger screens, up to
 * 64px from the top of the screen. Their width will take up 100% of the screen
 * width, up to a max of 960px. ContextSheets allow for vertical scrolling. If
 * for some reason you don't want the ContextSheet to stick to the bottom of the
 * screen on larger devices, pass`tabletAndDesktopCentered={true}`.
 *
 * If the content of your ContextSheet is dynamic (e.g., because it's a
 * multi-step wizard), you must pass a`contentKey` that uniquely identifies the
 * content. This lets the component adjust its height to fit the updated
 * content.
 *
 * Header Props
 *
 *
 * ----------------
 *
 * ContextSheets render header content based on props:
 *
 * *   The close icon renders when you pass `onRequestClose` (this is required)
 *
 * *   A back icon renders when you pass `onBackClick`
 *
 * *   On scroll, a divider appears between the header and the content
 *
 *
 * Touch Handle
 *
 *
 * ----------------
 *
 * On mobile, ContextSheets render a bar at the top that lets users drag the
 * sheet up and down. If the user drags the sheet down more than 20% of the
 * sheet's height, the sheet will dismiss itself (just like tapping the X icon
 * or the overlay).
 *
 * Accessibility
 *
 *
 * -----------------
 *
 * It's required to pass one of these props: `title`, `aria-labelledby`, or
 * `aria-label`. We recommend passing `title` (if you do that, the component
 * will render the appropriate `aria-labelledby` attribute for you).
 *
 * <Popover title="My Popover"> <Text>Content</Text> </Popover>
 *
 * <Popover aria-labelledby="popover-heading"> <Box> <Text
 * id="popover-heading">Heading</Text> <Text>Content</Text> </Box> </Popover>
 *
 * // Acceptable, but try to avoid because there's no visible text label
 * <Popover aria-label="Popover with Content"> <Image /> </Popover>
 *
 *
 * Analytics
 *
 *
 * -------------
 *
 * If you pass `analyticsName`, the Popover component will track:
 *
 * *   Modal impression events, if you also pass `trackBoxImpression={true}`
 *
 * *   `boxViewEvent: { targetName: "example" }`
 *
 * *   Back button events, if you also pass `onBackClick`
 *
 * *   `buttonViewEvent: { targetName: "example-back" }`
 *
 * *   `buttonHoverEvent: { targetName: "example-back" }`
 *
 * *   `buttonClickEvent: { targetName: "example-back" }`,
 *
 * *   Close button events
 *
 * *   `buttonViewEvent: { targetName: "example-close" }`
 *
 * *   `buttonHoverEvent: { targetName: "example-close" }`
 *
 * *   `buttonClickEvent: { targetName: "example-close" }`
 */
export const ContextSheet: React.FC<ContextSheetProps> = ({
  isOpen = false,
  onRequestClose,
  onBackClick,
  title,
  subtitle,
  headerIconButtonVariant,
  id,
  analyticsName,
  trackBoxImpression,
  trackButtonImpressions,
  children,
  contentKey,
  showScrollbar = true,
  hasPadding = true,
  tabletAndDesktopCentered,
  themeProps,
  contentBgColor,
  ...props
}) => {
  const [showDivider, setShowDivider] = useState(false);
  const handleScroll = useCallback<React.UIEventHandler<HTMLDivElement>>(
    (e) => setShowDivider(e.currentTarget.scrollTop > 0),
    [],
  );

  useDisabledBodyScroll(isOpen);

  const ariaLabelledBy = title ? getNavigationBarTitleId({ id }) : props['aria-labelledby'];
  const ariaLabel = ariaLabelledBy ? null : props['aria-label'];

  const { isMobile } = useDevice();

  const { radii, shadows, colors } = useTheme();

  const centered = !isMobile && tabletAndDesktopCentered;

  const contentStyles: React.CSSProperties = {
    background: contentBgColor ? colorKeys[contentBgColor] : colors.neutrals0,
    WebkitOverflowScrolling: 'touch',
    outline: 'none',
    border: 'none',
    position: 'fixed',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: centered ? 'auto' : 0,
    padding: 0,
    boxShadow: shadows.z2,
    borderRadius: centered ? radii.semiRounded : `${radii.semiRounded} ${radii.semiRounded} 0 0`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: isMobile ? `calc(100svh - ${MARGIN_TOP_MOBILE}px)` : 'calc(100svh - 64px)',
    width: centered ? 'auto' : '100%',
    maxWidth: '960px',
  };

  const contextSheetRef = useRef<HTMLDivElement>();
  const childrenContainerRef = useRef<HTMLDivElement>();
  const contextSheetHeight = useRef<number>(null);

  const animateToHeight = async (endHeight: number) => {
    const element = contextSheetRef.current;
    if (element) {
      return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          element.style.height = `${endHeight}px`;
        });
        element.ontransitionend = (e) => {
          if (e.propertyName === 'height') {
            element.ontransitionend = null;
            resolve();
          }
        };
      });
    }
  };

  // Read the context sheet's height once it's fully opened, and set it to a pixel value because
  // transitions don't work with height: auto
  useEffect(() => {
    let timeout: number;
    if (isOpen) {
      timeout = setTimeout(() => {
        contextSheetHeight.current = contextSheetRef.current.clientHeight;
        contextSheetRef.current.style.height = `${contextSheetHeight.current}px`;
      }, CLOSE_TIMEOUT_MS);
    } else {
      timeout = setTimeout(() => {
        contextSheetHeight.current = null;
      }, CLOSE_TIMEOUT_MS);
    }
    return () => clearTimeout(timeout);
  }, [isOpen]);

  const renderedChildren = useRef(children);
  const [hideChildren, setHideChildren] = useState(false);

  // Animate the height and opacity of the context sheet when the content changes
  useEffect(() => {
    const animateHeight = async () => {
      const contextSheet = contextSheetRef.current;
      const childrenContainer = childrenContainerRef.current;
      const child = childrenContainer?.firstElementChild;
      if (contextSheet && childrenContainer && child) {
        const headerHeight = contextSheet.clientHeight - childrenContainer.clientHeight;
        const totalContentHeight = headerHeight + child.scrollHeight;
        const maxHeight = window.innerHeight - MARGIN_TOP_MOBILE;
        await animateToHeight(totalContentHeight > maxHeight ? maxHeight : totalContentHeight);
        contextSheetHeight.current = contextSheetRef.current.clientHeight;
      }
    };

    setHideChildren(true);

    const timer = setTimeout(() => {
      renderedChildren.current = children;
      setHideChildren(false);
      animateHeight();
    }, OPACITY_TRANSITION_MS);

    return () => clearTimeout(timer);

    // `children` intentionally left out of dependencies array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
  };

  const globalStyles = css`
    .${OVERLAY_CLASS_NAME.base} {
      background-color: rgba(34, 34, 34, 0);
      transition: background-color ${CLOSE_TIMEOUT_MS}ms ease-out;
    }
    .${OVERLAY_CLASS_NAME.afterOpen} {
      background-color: rgba(34, 34, 34, 0.6);
    }
    .${OVERLAY_CLASS_NAME.beforeClose} {
      background-color: rgba(34, 34, 34, 0);
    }
    .${CONTENT_CLASS_NAME.base} {
      transform: translateY(100%);
      transition: transform ${CLOSE_TIMEOUT_MS}ms ease-out,
        height ${HEIGHT_TRANSITION_MS}ms ease-out;
    }
    .${CONTENT_CLASS_NAME.afterOpen} {
      transform: translateY(0);
    }
    .${CONTENT_CLASS_NAME.beforeClose} {
      transform: translateY(100%);
    }
  `;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={true}
      className={CONTENT_CLASS_NAME}
      overlayClassName={OVERLAY_CLASS_NAME}
      closeTimeoutMS={CLOSE_TIMEOUT_MS}
      style={{ content: contentStyles, overlay: overlayStyles }}
      contentRef={(node) => (contextSheetRef.current = node)}
      /*
        Accessibility note: Aria-model indicates the presence of a modal element
        https://www.w3.org/TR/wai-aria-1.2/#aria-modal
      */
      aria-modal={isOpen}
      /*
        Accessibility note: use the contentLabel prop to provide a label for the modal content
        If the modal is already labeled with visible text, you should specify the element including the label with the aria-labelledby attribute.
        https://reactcommunity.org/react-modal/accessibility/#aria-attributes
        To set aria-labelledby, either (1) pass headerProps.title and let this component set aria-labelledby for you, or (2) pass ariaLabelledBy
        and set it to the id of your custom label
      */
      aria={{ labelledby: ariaLabelledBy }}
      contentLabel={ariaLabel}
    >
      <Global styles={globalStyles} />

      {/*
          NOTE: Wrapping children in <ThemedLite /> since usually the modal is a
          sibling to any root App component that is wrapped in the theme
      */}
      <ThemedLite {...themeProps}>
        <>
          {isMobile && (
            <TouchHandle
              contextSheetRef={contextSheetRef}
              contextSheetHeight={contextSheetHeight}
              animateToHeight={animateToHeight}
              onRequestClose={onRequestClose}
            />
          )}
          {/* do not show modal header when none of these are present */}
          {(onRequestClose || onBackClick || title || subtitle) && (
            <ModalHeader
              trackBoxImpression={trackBoxImpression}
              trackButtonImpressions={trackButtonImpressions}
              analyticsName={analyticsName}
              onBackClick={onBackClick}
              onRequestClose={onRequestClose}
              title={title}
              subtitle={subtitle}
              id={id}
              showDivider={showDivider}
              headerIconButtonVariant={headerIconButtonVariant}
            />
          )}
        </>

        <Box
          flexGrow={1}
          overflowY="scroll"
          sx={
            showScrollbar
              ? {}
              : {
                  '&::-webkit-scrollbar': { display: 'none' },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }
          }
          onScroll={handleScroll}
          ref={childrenContainerRef}
        >
          <Box
            opacity={hideChildren ? 0 : 1}
            transition={`opacity ${OPACITY_TRANSITION_MS}ms ease-out`}
            transitionDelay={!hideChildren && `${HEIGHT_TRANSITION_MS}ms`}
            p={hasPadding ? 5 : 0}
          >
            {renderedChildren.current}
          </Box>
        </Box>
      </ThemedLite>
    </ReactModal>
  );
};

const TouchHandle = ({ contextSheetRef, contextSheetHeight, animateToHeight, onRequestClose }) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(null);
  const touchMoveY = useRef<number>(null);

  let originalTransition: string;

  // Prevent pull-to-refresh
  useEffect(() => {
    handleRef.current.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
      },
      { passive: false },
    );
  }, []);

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = (e) => {
    touchStartY.current = e.touches[0].pageY;
    // Remove the original transition so height changes match the touch movements exactly
    originalTransition = contextSheetRef.current.style.transition;
    requestAnimationFrame(() => {
      contextSheetRef.current.style.transition = 'none';
    });
  };

  const handleTouchMove: TouchEventHandler = (e) => {
    touchMoveY.current = e.touches[0].pageY;
    requestAnimationFrame(() => {
      contextSheetRef.current.style.height = `${
        contextSheetHeight.current + touchStartY.current - touchMoveY.current
      }px`;
    });
  };

  const handleTouchEnd = async () => {
    // Restore the original transition
    requestAnimationFrame(() => {
      contextSheetRef.current.style.transition = originalTransition;
    });
    if (touchMoveY.current - touchStartY.current > contextSheetHeight.current * 0.2) {
      // User has dragged the sheet down more than 20% of height, so close it
      await animateToHeight(0);
      onRequestClose();
    } else {
      // User hasn't dragged the sheet down far enough, so reset it
      await animateToHeight(contextSheetHeight.current);
    }
    touchStartY.current = null;
    touchMoveY.current = null;
  };

  return (
    <Box display="flex" justifyContent="center">
      <Box
        px="10"
        py="4"
        mb="-12px" // Bottom padding + negative margin makes tappable region larger without creating extra visible space
        zIndex={1}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={handleRef}
      >
        <Box
          backgroundColor="neutrals20"
          width="48px"
          height="6px"
          borderRadius="semiRounded"
          userSelect="none"
        ></Box>
      </Box>
    </Box>
  );
};
